import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Image, Upload, Video, Star, Euro, Trash2, Edit, Eye, EyeOff, 
  PlusCircle, MoreVertical, ImagePlus, Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  getEscortPhotoDetails,
  deleteProfileImage as deleteImageService,
  setMainProfileImage,
  saveProfileImageToDatabase,
  EscortPhoto
} from '@/services/profileImage/databaseService';
import { uploadImageFile } from '@/services/profileImage/storageService';
import { useProfileVideos, UIVideo } from '@/hooks/useProfileVideos';

interface MediaItem {
  id: string;
  url: string;
  type: 'photo' | 'video' | 'premium';
  isPublic: boolean;
  isMain?: boolean;
  createdAt: string;
  storagePath: string | null;
}

const EmptyState = ({ type, onAdd }: { type: string, onAdd: () => void }) => {
  const { t } = useLanguage();
  
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-4">
        {type === 'photos' && <Image className="h-10 w-10 text-muted-foreground" />}
        {type === 'videos' && <Video className="h-10 w-10 text-muted-foreground" />}
        {type === 'premium' && <Star className="h-10 w-10 text-muted-foreground" />}
      </div>
      <h3 className="text-lg font-medium mb-2">{t('escort.media.no_media')}</h3>
      <p className="text-sm text-muted-foreground text-center max-w-xs mb-4">
        {t('escort.media.drag_drop')}
      </p>
      <Button onClick={onAdd}>
        <Upload className="h-4 w-4 mr-2" />
        {type === 'photos' && t('escort.media.upload_photo')}
        {type === 'videos' && t('escort.media.upload_video')}
        {type === 'premium' && t('escort.media.upload_premium')}
      </Button>
    </div>
  );
};

const MediaGrid = ({ items, onDelete, onToggleVisibility, onSetAsMain, type = 'photo' }: {
  items: MediaItem[],
  onDelete: (item: MediaItem) => void,
  onToggleVisibility: (item: MediaItem) => void,
  onSetAsMain: (item: MediaItem) => void,
  type?: 'photo' | 'video'
}) => {
  const { t } = useLanguage();
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <div key={item.id} className="relative group aspect-square rounded-md overflow-hidden bg-muted">
          {type === 'photo' ? (
             <img
               src={item.url}
               alt=""
               className="w-full h-full object-cover"
             />
           ) : (
             <video
               src={item.url}
               className="w-full h-full object-cover bg-black"
               muted
               loop
               playsInline
             >
               Votre navigateur ne supporte pas la balise vidéo.
             </video>
           )}
          
          {item.isMain && (
            <div className={`absolute top-2 left-2 ${type === 'video' ? 'bg-blue-500' : 'bg-amber-500'} text-white text-xs px-2 py-0.5 rounded-full`}>
              {type === 'video' ? t('escort.media.preview') : t('escort.media.main')}
            </div>
          )}
          
          <div className="absolute top-2 right-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-black/20 hover:bg-black/40 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!item.isMain && (
                  <DropdownMenuItem onClick={() => onSetAsMain(item)}>
                    <Star className="h-4 w-4 mr-2" />
                    {type === 'video' ? t('escort.media.set_as_preview') : t('escort.media.set_as_main')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onToggleVisibility(item)}>
                  {item.isPublic ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      {t('escort.media.set_as_private')}
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      {t('escort.media.set_as_public')}
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(item)} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('escort.media.delete_media')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      ))}
      
      <div className="aspect-square rounded-md border-2 border-dashed flex items-center justify-center">
        <Button variant="ghost" className="h-full w-full flex flex-col items-center gap-2">
          <ImagePlus className="h-6 w-6" />
          <span>{t('escort.media.upload_photo')}</span>
        </Button>
      </div>
    </div>
  );
};

const UploadForm = ({ onCancel, onSubmit }: { onCancel: () => void, onSubmit: (files: File[], description: string, isPremium: boolean, price: number) => void }) => {
  const { t } = useLanguage();
  const [files, setFiles] = useState<File[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files));
    }
  };
  
  const handleSubmitClick = async () => {
    setUploading(true);
    try {
      await onSubmit(files, description, isPremium, parseFloat(price) || 0);
    } catch (error) {
      console.error("Upload failed in form:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('escort.media.upload')}</CardTitle>
        <CardDescription>
          {t('escort.media.drag_drop')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors">
            <input
              type="file"
              id="media-upload"
              className="hidden"
              accept="image/jpeg,image/png,image/jpg,video/mp4"
              multiple
              onChange={handleFileChange}
              aria-label={t('escort.media.upload_media_label')}
            />
            <Label htmlFor="media-upload" className="cursor-pointer">
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground mb-1">
                {files.length > 0 
                  ? `${files.length} fichier(s) sélectionné(s)` 
                  : t('escort.media.drag_drop')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('escort.media.supported_formats')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('escort.media.file_size_limit')}
              </p>
            </Label>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="description">{t('escort.media.description')}</Label>
              <Textarea 
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Décrivez ce que vous téléchargez"
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-premium"
                checked={isPremium}
                onChange={(e) => setIsPremium(e.target.checked)}
                className="h-4 w-4"
                aria-label={t('escort.media.sell_content_label')}
              />
              <Label htmlFor="is-premium" className="cursor-pointer">
                {t('escort.media.sell_content')}
              </Label>
            </div>
            
            {isPremium && (
              <div>
                <Label htmlFor="price">{t('escort.media.price')} (€)</Label>
                <div className="relative mt-1">
                  <Euro className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="price"
                    type="number"
                    placeholder="5.99"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="pl-9"
                    min={0}
                    step={0.01}
                    aria-label={t('escort.media.price')}
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onCancel}>
              {t('escort.security.cancel')}
            </Button>
            <Button onClick={handleSubmitClick} disabled={uploading || files.length === 0}>
              {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
               ) : (
                  <Upload className="h-4 w-4 mr-2" />
               )}
              {uploading ? t('escort.media.uploading') : t('escort.media.upload')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EscortMedia: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [activeTab, setActiveTab] = useState('photos');
  
  const [escortPhotos, setEscortPhotos] = useState<EscortPhoto[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(true);
  const [loadingPhotosError, setLoadingPhotosError] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  const { 
    videos: profileVideos,
    isLoading: isVideosLoading,
    loadingError: videoLoadingError,
    uploadError: videoUploadError,
    isUploading: isUploadingVideo,
    uploadVideo,
    deleteVideo,
    updateVideoMeta,
    setAsPreview,
  } = useProfileVideos();

  const isLoading = isLoadingPhotos || isVideosLoading;
  const isUploading = isUploadingPhoto || isUploadingVideo;
  const error = loadingPhotosError || videoLoadingError;

  const loadEscortPhotos = useCallback(async () => {
    if (!user) return;
    setIsLoadingPhotos(true);
    setLoadingPhotosError(null);
    try {
      const photosData = await getEscortPhotoDetails(user.id);
      setEscortPhotos(photosData);
    } catch (err) {
      console.error("Error loading escort photos:", err);
      const message = (err instanceof Error) ? err.message : 'Unknown error';
      setLoadingPhotosError(message);
      toast({ title: t('common.error'), description: t('escort.media.load_photos_error', { message }), variant: 'destructive' });
    } finally {
      setIsLoadingPhotos(false);
    }
  }, [user, t, toast]);

  useEffect(() => {
    loadEscortPhotos();
  }, [loadEscortPhotos]);
  
  const photosForGrid: MediaItem[] = escortPhotos.map(photo => ({
    id: photo.id,
    url: photo.photo_url,
    type: 'photo',
    isPublic: !photo.is_private,
    isMain: photo.is_main,
    createdAt: photo.created_at,
    storagePath: photo.storage_path,
  }));

  const handleDeletePhoto = async (item: MediaItem) => {
    if (!user) return;
    const photoToDelete = escortPhotos.find(p => p.id === item.id);
    if (!photoToDelete) return;

    setEscortPhotos(prev => prev.filter(p => p.id !== item.id));

    try {
        const success = await deleteImageService(user.id, photoToDelete.photo_url);
        if (success) {
            toast({ title: t('common.success'), description: t('escort.media.photo_deleted') });
        } else {
            throw new Error('Deletion failed in service');
        }
    } catch (err) {
        console.error("Error deleting photo:", err);
        setEscortPhotos(prev => [...prev, photoToDelete]);
        toast({ title: t('common.error'), description: t('escort.media.delete_photo_error', { message: (err as Error).message }), variant: 'destructive' });
    }
  };

  const handleTogglePhotoVisibility = (item: MediaItem) => {
    console.log("Toggle photo visibility", item.id);
    toast({title: "Fonctionnalité Non Implémentée", description: "La gestion de la visibilité des photos sera ajoutée."});
  };

  const handleSetAsMainPhoto = async (item: MediaItem) => {
     if (!user) return;
     console.log(`Setting photo ${item.id} as main for escort ${user.id}`);
     
     const previousPhotos = [...escortPhotos];
     setEscortPhotos(prev => prev.map(p => ({
         ...p,
         is_main: p.id === item.id
     })));
     
     try {
       const success = await setMainProfileImage(user.id, item.id);
       if (success) {
         toast({ 
           title: t('common.success'), 
           description: t('escort.media.main_photo_set_success') 
         });
       } else {
         throw new Error('Failed to set main photo in service');
       }
     } catch (err) {
       console.error('Error setting main photo:', err);
       setEscortPhotos(previousPhotos);
       toast({ 
         title: t('common.error'), 
         description: t('escort.media.set_main_photo_error', { message: (err as Error).message }), 
         variant: 'destructive' 
       });
     }
  };
  
  const videosForGrid: MediaItem[] = profileVideos.map(v => ({
      id: v.id,
      url: v.url,
      type: 'video',
      isPublic: !v.isPrivate,
      isMain: v.isPreview,
      createdAt: v.createdAt,
      storagePath: v.storagePath,
  }));

  const handleDeleteVideoAction = (item: MediaItem) => {
    deleteVideo(item.id, item.storagePath);
  };
  const handleToggleVideoVisibility = (item: MediaItem) => {
    updateVideoMeta(item.id, { is_private: item.isPublic });
  };
  const handleSetAsPreviewVideo = (item: MediaItem) => {
    setAsPreview(item.id);
  };

  const handleOpenUpload = (tab: string) => {
    setActiveTab(tab);
    setShowUploadForm(true);
  };

  const handleCancelUpload = () => {
    setShowUploadForm(false);
  };

  const handleUploadSubmit = async (files: File[], description: string, isPremium: boolean, price: number) => {
    if (!user || files.length === 0) return;
    setIsUploadingPhoto(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
        try {
            console.log(`📤 Uploading photo for ${user.id}`);
            const uploadResult = await uploadImageFile(user.id, file);
            if (!uploadResult.success || !uploadResult.url) {
                throw new Error(uploadResult.error || 'Upload failed');
            }
            const imageUrl = uploadResult.url;
            const isFirstPhoto = escortPhotos.length === 0 && successCount === 0;
            await saveProfileImageToDatabase(user.id, imageUrl, isFirstPhoto);
            console.log(`✅ Photo uploaded and saved to db for ${user.id}`);
            successCount++;
        } catch (err) {
            console.error('Error uploading one photo:', err);
            errorCount++;
        }
    }

    setIsUploadingPhoto(false);
    setShowUploadForm(false);

    if (successCount > 0) {
        toast({ title: t('common.success'), description: t('escort.media.upload_success', { count: successCount }) });
        loadEscortPhotos();
    }
    if (errorCount > 0) {
        toast({ title: t('common.error'), description: t('escort.media.upload_error', { count: errorCount }), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-8">
      {isLoading && (
         <div className="flex justify-center items-center h-40">
           <Loader2 className="h-8 w-8 animate-spin" />
         </div>
       )}
      {error && <p className="text-red-500">{t('common.error_loading_data')}: {typeof error === 'string' ? error : t('common.unknown_error')}</p>}

      {!isLoading && !error && (
        <>
          <h2 className="text-2xl font-semibold">{t('escort.media.title')}</h2>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="photos" className="flex items-center">
                <Image className="h-4 w-4 mr-2" />
                {t('escort.media.public_photos')}
              </TabsTrigger>
              <TabsTrigger value="videos" className="flex items-center">
                <Video className="h-4 w-4 mr-2" />
                {t('escort.media.videos')}
              </TabsTrigger>
              <TabsTrigger value="premium" className="flex items-center">
                <Star className="h-4 w-4 mr-2" />
                {t('escort.media.premium_content')}
              </TabsTrigger>
            </TabsList>
            
            <div className="mb-4 flex justify-end">
              <Button onClick={() => setShowUploadForm(true)} disabled={isUploading}>
                 {isUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                 {t('escort.media.upload')}
              </Button>
            </div>
            
            {showUploadForm && (
                <UploadForm 
                    onCancel={handleCancelUpload} 
                    onSubmit={handleUploadSubmit}
                />
            )}

            <TabsContent value="photos">
              <Card>
                <CardHeader>
                  <CardTitle>{t('escort.media.public_photos')}</CardTitle>
                  <CardDescription>
                    Ces photos sont visibles par tous les utilisateurs du site.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingPhotos ? (
                    <div className="flex justify-center items-center h-40">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : loadingPhotosError ? (
                    <p className="text-red-500">{t('common.error_loading_data')}: {loadingPhotosError}</p>
                  ) : photosForGrid.length > 0 ? (
                    <MediaGrid
                      items={photosForGrid}
                      onDelete={handleDeletePhoto}
                      onToggleVisibility={handleTogglePhotoVisibility}
                      onSetAsMain={handleSetAsMainPhoto}
                      type="photo"
                    />
                  ) : (
                    <EmptyState type="photos" onAdd={() => handleOpenUpload('photos')} />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="videos">
              <Card>
                <CardHeader>
                  <CardTitle>{t('escort.media.videos')}</CardTitle>
                  <CardDescription>
                    Téléchargez des vidéos courtes pour présenter votre profil.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isVideosLoading ? (
                    <div className="flex justify-center items-center h-40">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : videoLoadingError ? (
                    <p className="text-red-500">{t('common.error_loading_data')}: {videoLoadingError}</p>
                  ) : profileVideos.length === 0 ? (
                    <EmptyState type="videos" onAdd={() => handleOpenUpload('videos')} />
                  ) : (
                    <MediaGrid
                      items={videosForGrid}
                      onDelete={handleDeleteVideoAction}
                      onToggleVisibility={handleToggleVideoVisibility}
                      onSetAsMain={handleSetAsPreviewVideo}
                      type="video"
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="premium">
              <Card>
                <CardHeader>
                  <CardTitle>{t('escort.media.premium_content')}</CardTitle>
                  <CardDescription>
                    Contenu exclusif que les clients peuvent acheter.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <EmptyState type="premium" onAdd={() => handleOpenUpload('premium')} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default EscortMedia;
